import { UnitConceptRow, FunnelStages, Period } from '../funnel-detail/types';
import { calculateUnitValues } from '../funnel-detail/utils';
import { FunnelContext } from './types';

export function buildFunnelContext(args: {
  funnelId?: string;
  funnelName: string;
  productName: string;
  period: Period;
  stages: FunnelStages;
  unitRows: UnitConceptRow[];
  expectedProfitPct: number;
  realDeliveries: number;
}): FunnelContext {
  const { funnelId, funnelName, productName, period, stages, unitRows, expectedProfitPct, realDeliveries } = args;

  const get = (id: UnitConceptRow['id']): number => {
    const row = unitRows.find(r => r.id === id);
    return row ? (row.realValue ?? 0) : 0;
  };

  const getTarget = (id: UnitConceptRow['id']): number => {
    const row = unitRows.find(r => r.id === id);
    return row ? row.targetValue : 0;
  };

  const realDerived = calculateUnitValues(unitRows, false);
  const targetDerived = calculateUnitValues(unitRows, true, expectedProfitPct);

  const realRevenue = realDerived.effectivePrice * realDeliveries;
  const realProductCost = get('unit-product-cost') * realDeliveries;
  const realFreight = get('unit-shipping-cost') * realDeliveries;
  const realReturns = realDerived.returnsAmount * realDeliveries;
  const realCpaTotal = get('unit-cpa') * realDeliveries;
  const realTotalCosts = realProductCost + realFreight + realReturns;
  const realProfit = realRevenue - realTotalCosts - realCpaTotal;
  const realProfitPct = realRevenue > 0 ? (realProfit / realRevenue) * 100 : 0;

  return {
    funnelId,
    funnelName,
    productName,
    period: { label: period.label, days: period.days },
    stages,
    unitEconomics: {
      productCost: get('unit-product-cost'),
      shippingAndFreightCost: get('unit-shipping-cost'),
      returnsRate: get('unit-returns-pct'),
      returnsAmount: realDerived.returnsAmount,
      cpa: get('unit-cpa'),
      salePrice: realDerived.effectivePrice,
      totalCosts: realDerived.totalCosts,
      finalProfit: realDerived.finalProfit,
      finalProfitPct: realDerived.finalProfitPct,
    },
    unitTargets: {
      shippingAndFreightCost: getTarget('unit-shipping-cost'),
      returnsRate: getTarget('unit-returns-pct'),
      cpa: getTarget('unit-cpa'),
      expectedProfitPct,
      targetSalePrice: targetDerived.effectivePrice,
      targetCpaMaxProfitable: Math.max(0, targetDerived.effectivePrice - targetDerived.totalCosts),
    },
    funnelTotals: {
      revenue: Math.round(realRevenue),
      totalCosts: Math.round(realTotalCosts),
      cpa: Math.round(realCpaTotal),
      profit: Math.round(realProfit),
      profitPct: realProfitPct,
    },
  };
}
